import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  Pencil,
  X,
  Palette,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import {
  listClasses,
  addClass,
  updateClass,
  deleteClass,
} from "@/lib/api";
import { useClasses } from "@/contexts/ClassesContext";

const COLOR_PRESETS = [
  "#f43f5e", "#f97316", "#eab308", "#84cc16", "#10b981",
  "#14b8a6", "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1",
  "#8b5cf6", "#a855f7", "#d946ef", "#ec4899", "#f59e0b",
];

export default function AdminClasses() {
  const { refresh: refreshContext } = useClasses();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLOR_PRESETS[0]);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const list = await listClasses();
      setRows(list);
    } catch (e) {
      toast.error("Gagal memuat daftar kelas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (row) => {
    setEditingName(row.name);
    setEditName(row.name);
    setEditColor(row.color);
  };

  const cancelEdit = () => {
    setEditingName(null);
    setEditName("");
    setEditColor("");
  };

  const saveEdit = async () => {
    if (!editName.trim()) {
      toast.error("Nama kelas tidak boleh kosong");
      return;
    }
    try {
      await updateClass(editingName, { name: editName.trim(), color: editColor });
      toast.success(`Kelas "${editingName}" diperbarui`);
      cancelEdit();
      await load();
      await refreshContext();
    } catch (e) {
      toast.error("Gagal update", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    }
  };

  const doAdd = async () => {
    if (!newName.trim()) {
      toast.error("Nama kelas wajib diisi");
      return;
    }
    setAdding(true);
    try {
      const res = await addClass(newName.trim(), newColor);
      if (res.created) {
        toast.success(`Kelas "${res.name}" ditambahkan`);
      } else {
        toast.info(`Kelas "${res.name}" sudah ada — warna diperbarui`);
      }
      setNewName("");
      setNewColor(COLOR_PRESETS[Math.floor(Math.random() * COLOR_PRESETS.length)]);
      await load();
      await refreshContext();
    } catch (e) {
      toast.error("Gagal menambah", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setAdding(false);
    }
  };

  const doDelete = async (row) => {
    const inUse = row.total_count > 0;
    const confirmMsg = inUse
      ? `Kelas "${row.name}" masih dipakai di ${row.total_count} temuan. Hapus PAKSA? (data tidak akan hilang, hanya label dihapus dari daftar)`
      : `Hapus kelas "${row.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    setDeleting(row.name);
    try {
      await deleteClass(row.name, inUse);
      toast.success(`Kelas "${row.name}" dihapus`);
      await load();
      await refreshContext();
    } catch (e) {
      toast.error("Gagal hapus", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setDeleting(null);
    }
  };

  const autoCount = rows.filter((r) => r.auto_discovered).length;

  return (
    <div className="max-w-5xl mx-auto" data-testid="admin-classes-page">
      <header className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.14em] text-indigo-400 font-semibold mb-2">
          Admin · Class Management
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-medium text-zinc-50 mb-3">
          Kelola Daftar Kelas
        </h1>
        <p className="text-zinc-400 max-w-2xl text-sm sm:text-base leading-relaxed">
          Setiap kelas baru dari Roboflow akan otomatis terdaftar di sini dengan warna
          random dari palet. Anda bisa mengganti nama/warna atau menambah kelas manual.
        </p>
      </header>

      {/* Add new class */}
      <div className="mb-6 rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-indigo-400" />
          <div className="text-[10px] uppercase tracking-[0.14em] text-indigo-300 font-semibold">
            Tambah Kelas Manual
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-zinc-400 block mb-1.5">Nama Kelas</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="misal: Pneumothoraks"
              data-testid="new-class-name-input"
              className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1.5">Warna</label>
            <ColorPicker value={newColor} onChange={setNewColor} testId="new-class-color" />
          </div>
          <button
            onClick={doAdd}
            disabled={adding || !newName.trim()}
            data-testid="add-class-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2 text-sm font-medium shadow-lg shadow-indigo-500/20"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Tambah
          </button>
        </div>
      </div>

      {/* Stats banner */}
      <div className="mb-4 flex items-center gap-6 text-xs text-zinc-500">
        <span>
          Total: <span className="text-zinc-100 font-semibold">{rows.length}</span> kelas
        </span>
        {autoCount > 0 && (
          <span>
            Auto-discovered: <span className="text-indigo-300 font-semibold">{autoCount}</span>
          </span>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-900 overflow-hidden">
        <div className="grid grid-cols-[60px_1fr_120px_120px_120px] gap-3 px-5 py-3 bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
          <div>Warna</div>
          <div>Nama Kelas</div>
          <div className="text-right">AI Predictions</div>
          <div className="text-right">Koreksi</div>
          <div className="text-right">Aksi</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            Belum ada kelas terdaftar
          </div>
        ) : (
          <div className="divide-y divide-zinc-900">
            {rows.map((row) => {
              const isEditing = editingName === row.name;
              return (
                <div
                  key={row.name}
                  className="grid grid-cols-[60px_1fr_120px_120px_120px] gap-3 px-5 py-3.5 items-center hover:bg-zinc-900/30"
                  data-testid={`class-row-${row.name}`}
                >
                  {isEditing ? (
                    <>
                      <ColorPicker value={editColor} onChange={setEditColor} testId="edit-color" />
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        data-testid="edit-name-input"
                        className="w-full rounded border border-indigo-500/40 bg-zinc-950 px-2 py-1 text-sm text-zinc-100 focus:border-indigo-500 outline-none"
                      />
                      <div className="text-xs text-right text-zinc-500 font-mono">
                        {row.ai_predictions_count}
                      </div>
                      <div className="text-xs text-right text-zinc-500 font-mono">
                        {row.corrections_count}
                      </div>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={saveEdit}
                          data-testid="save-edit-btn"
                          className="inline-flex items-center gap-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white px-2 py-1 text-[11px] font-medium"
                        >
                          <Save className="w-3 h-3" /> Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          data-testid="cancel-edit-btn"
                          className="p-1 rounded text-zinc-500 hover:text-zinc-100"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="w-6 h-6 rounded"
                        style={{ backgroundColor: row.color }}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-zinc-100 truncate">
                            {row.name}
                          </span>
                          {row.auto_discovered && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
                              Auto
                            </span>
                          )}
                          {row.name === "normal" && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
                              Healthy
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-zinc-600 font-mono mt-0.5">
                          {row.color}
                          {row.created_at && ` · ${new Date(row.created_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <div className="text-xs text-right text-zinc-300 font-mono">
                        {row.ai_predictions_count.toLocaleString()}
                      </div>
                      <div className="text-xs text-right text-zinc-300 font-mono">
                        {row.corrections_count.toLocaleString()}
                      </div>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(row)}
                          data-testid={`edit-class-${row.name}-btn`}
                          className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"
                          title="Rename / ganti warna"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => doDelete(row)}
                          disabled={deleting === row.name}
                          data-testid={`delete-class-${row.name}-btn`}
                          className="p-1.5 rounded hover:bg-rose-500/10 text-zinc-500 hover:text-rose-400 disabled:opacity-40"
                          title={row.total_count > 0 ? `Terpakai di ${row.total_count} temuan` : "Hapus"}
                        >
                          {deleting === row.name ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Warning notice */}
      <div className="mt-6 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-200/80 leading-relaxed">
          <strong className="text-amber-300">Catatan:</strong> Rename tidak otomatis mengubah
          nama kelas di data lama (predictions/corrections). Hapus kelas dengan{" "}
          <em>force</em> hanya menghapus label dari daftar — data pemeriksaan yang mengandung
          kelas tersebut tetap ada, tapi warnanya akan jadi default.
        </div>
      </div>
    </div>
  );
}

function ColorPicker({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className="inline-flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-2 py-2 text-xs text-zinc-300 hover:border-zinc-700"
      >
        <span
          className="w-4 h-4 rounded"
          style={{ backgroundColor: value }}
        />
        <Palette className="w-3 h-3 text-zinc-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 mt-1 rounded-lg border border-zinc-800 bg-zinc-950 p-2 shadow-2xl z-30 w-56">
            <div className="grid grid-cols-5 gap-1.5">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                  data-testid={`color-preset-${c}`}
                  className={`w-8 h-8 rounded ${
                    value === c ? "ring-2 ring-white ring-offset-2 ring-offset-zinc-950" : ""
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-900">
              <label className="text-[10px] text-zinc-500 block mb-1">Custom HEX:</label>
              <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#ff0000"
                className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs font-mono text-zinc-100 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
